import { Icon, useObsinaI18n } from "@obsina/design-system";
import {
  AlertTriangle,
  Check,
  CircleHelp,
  OctagonAlert,
  RefreshCw,
} from "lucide-react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import type { CSSProperties, ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import { activityMessages } from "./messages";
import { clockTime, relativeTime } from "./model";
import type { ActivitySnapshot, ActivityStateKind } from "./model";
import "./activity.css";

const ActivityContext = createContext<ActivitySnapshot | null>(null);

/**
 * Publishes the current activity observation to every page. The value is derived
 * from the control-plane summary the application already polls, so mounting this
 * costs no additional request anywhere.
 */
export function ActivityProvider({
  snapshot,
  children,
}: {
  snapshot: ActivitySnapshot | null;
  children: ReactNode;
}) {
  return (
    <ActivityContext.Provider value={snapshot}>
      {children}
    </ActivityContext.Provider>
  );
}

/**
 * The shell's activity slot. Renders nothing when there is nothing to observe
 * (no summary yet, no connection, or a surface mounted outside the provider),
 * so no page ever carries an empty frame.
 */
export function SidebarActivity() {
  const snapshot = useContext(ActivityContext);
  if (!snapshot) return null;
  return <ActivityIndicator snapshot={snapshot} />;
}

// Four distinct outlines, so the state is legible without colour: an octagon
// for a stop, a question mark for an unknown, a triangle for stopped items, a
// tick for a clear backlog, and a rotating arrow for work in flight.
const stateIcon: Record<ActivityStateKind, LucideIcon> = {
  blocked: OctagonAlert,
  unknown: CircleHelp,
  working: RefreshCw,
  unfinished: AlertTriangle,
  idle: Check,
};

interface PanelPlacement {
  readonly left: number;
  readonly width: number;
  readonly top?: number;
  readonly bottom?: number;
}

/**
 * A very small, always-present sign of what the system is doing with the
 * member's material: one icon and one short line in the rail's footer, with a
 * compact breakdown behind it.
 *
 * It is an observation, never a command — there is no control here that starts,
 * stops or retries anything, and no queue, job or worker is named. Status is
 * carried by words and by glyph shape, never by colour alone, and the panel
 * always states the time the observation was read so a stale reading can never
 * pass for a live one.
 */
export function ActivityIndicator({
  snapshot,
}: {
  snapshot: ActivitySnapshot;
}) {
  const { locale } = useObsinaI18n();
  const copy = activityMessages;
  const [open, setOpen] = useState(false);
  const [placement, setPlacement] = useState<PanelPlacement | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelId = useId();

  // A single wall-clock reading per snapshot: the relative distances below stay
  // stable within a render pass and only move when a new observation arrives.
  const now = useMemo(() => new Date(), [snapshot]);
  const asOfText = clockTime(snapshot.asOf, locale);
  const lastProcessed = snapshot.lastCompletedAt
    ? relativeTime(snapshot.lastCompletedAt, now, locale)
    : null;

  const triggerLabel =
    snapshot.kind === "blocked"
      ? copy.trigger.blocked
      : snapshot.kind === "unknown"
        ? copy.trigger.unknown
        : snapshot.kind === "working"
          ? copy.trigger.working(snapshot.inProgress + snapshot.waiting)
          : snapshot.kind === "unfinished"
            ? copy.trigger.unfinished(snapshot.unfinished)
            : copy.trigger.idle;

  // Always the movement, never the blocker: the blocked lines carry the blocker
  // and this sentence answers the separate question of what is still running.
  const stateSentence =
    snapshot.flow === "unknown"
      ? copy.detail.unknown
      : snapshot.flow === "working"
        ? copy.detail.working(snapshot.inProgress, snapshot.waiting)
        : snapshot.flow === "unfinished"
          ? copy.detail.unfinished(snapshot.unfinished)
          : copy.detail.idle;

  // One line per stopped area, with the shared reason left to the heading.
  const blockerLines = snapshot.blockers.map((blocker) => {
    const area = copy.stage[blocker.stage];
    if (blocker.needsAccountRenewal) return copy.blocked.renewal(area);
    const since = relativeTime(blocker.since, now, locale);
    return since
      ? copy.blocked.area(area, since)
      : copy.blocked.areaUndated(area);
  });

  // Stopped work while something is still moving: the state sentence describes
  // the movement, so the terminal count needs its own line. When nothing is
  // moving the state sentence already is that count, and it is not repeated.
  const unfinishedLine =
    snapshot.flow === "working" && snapshot.unfinished > 0
      ? copy.detail.unfinished(snapshot.unfinished)
      : null;

  // The visible label leads the accessible name (so speaking the label always
  // activates the control), and the rest of the observation follows for readers
  // who never see the panel.
  const accessibleName = [
    triggerLabel,
    // A reader who never opens the panel still hears which areas stopped; the
    // trigger only has room to say that something did.
    ...blockerLines,
    unfinishedLine,
    asOfText ? copy.detail.asOf(asOfText) : null,
    copy.detailsHint,
  ]
    .filter((part): part is string => Boolean(part))
    .join(". ");

  const place = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger || typeof window === "undefined") return;
    const rect = trigger.getBoundingClientRect();
    const width = Math.min(300, Math.max(200, window.innerWidth - 16));
    const left = Math.max(
      8,
      Math.min(rect.left, window.innerWidth - width - 8),
    );
    // The rail sits at the bottom on wide viewports and at the top when it
    // reflows into a bar, so the panel opens towards whichever side has room.
    const openUpwards = rect.top > window.innerHeight - rect.bottom;
    setPlacement({
      left,
      width,
      ...(openUpwards
        ? { bottom: Math.max(8, window.innerHeight - rect.top + 6) }
        : { top: rect.bottom + 6 }),
    });
  }, []);

  useEffect(() => {
    if (!open) return;
    place();
    const close = (event: Event) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setOpen(false);
      triggerRef.current?.focus();
    };
    document.addEventListener("pointerdown", close);
    document.addEventListener("keydown", onKeyDown);
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      document.removeEventListener("pointerdown", close);
      document.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open, place]);

  const panelStyle: CSSProperties | undefined = placement
    ? {
        insetInlineStart: `${placement.left}px`,
        inlineSize: `${placement.width}px`,
        ...(placement.top === undefined
          ? { insetBlockEnd: `${placement.bottom}px` }
          : { insetBlockStart: `${placement.top}px` }),
      }
    : undefined;

  return (
    <div className="sidebar-activity" ref={rootRef}>
      <button
        aria-controls={panelId}
        aria-expanded={open}
        aria-label={accessibleName}
        className="sidebar-activity__trigger"
        data-state={snapshot.kind}
        onClick={() => setOpen((value) => !value)}
        ref={triggerRef}
        type="button"
      >
        <Icon
          className="sidebar-activity__glyph"
          icon={stateIcon[snapshot.kind]}
          size={14}
        />
        <span className="sidebar-activity__label">{triggerLabel}</span>
        {snapshot.kind === "working" && snapshot.unfinished > 0 && (
          <Icon
            className="sidebar-activity__warn"
            icon={AlertTriangle}
            size={12}
          />
        )}
      </button>
      {open && (
        <div
          aria-label={copy.label}
          className="sidebar-activity__panel"
          id={panelId}
          role="group"
          style={panelStyle}
        >
          {blockerLines.length > 0 && (
            <div className="sidebar-activity__blocked">
              <p className="sidebar-activity__blocked-heading">
                {copy.blocked.heading}{" "}
                <span className="sidebar-activity__blocked-reason">
                  {copy.blocked.notYourDoing}
                </span>
              </p>
              <ul className="sidebar-activity__blocked-areas">
                {blockerLines.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            </div>
          )}
          {/* Never two headings in a row: with a blocker present the movement
              sentence is the secondary fact and is set as one. */}
          <p
            className={
              blockerLines.length > 0
                ? "sidebar-activity__note"
                : "sidebar-activity__state"
            }
          >
            {stateSentence}
          </p>
          {snapshot.stages.length > 0 && (
            <ul className="sidebar-activity__stages">
              {snapshot.stages.map((stage) => (
                <li key={stage.key}>
                  <span className="sidebar-activity__stage-name">
                    {copy.stage[stage.key]}
                  </span>
                  <span className="sidebar-activity__stage-counts">
                    {stage.inProgress > 0 && (
                      <span>
                        {copy.detail.stageInProgress(stage.inProgress)}
                      </span>
                    )}
                    {stage.waiting > 0 && (
                      <span>{copy.detail.stageWaiting(stage.waiting)}</span>
                    )}
                    {stage.unfinished > 0 && (
                      <span data-tone="danger">
                        {copy.detail.stageUnfinished(stage.unfinished)}
                      </span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          )}
          {unfinishedLine && (
            <p className="sidebar-activity__note" data-tone="danger">
              {unfinishedLine}
            </p>
          )}
          {snapshot.recentCompleted > 0 && (
            <p className="sidebar-activity__note">
              {copy.detail.recent(snapshot.recentCompleted)}
            </p>
          )}
          {lastProcessed && (
            <p className="sidebar-activity__note">
              {copy.detail.lastProcessed(lastProcessed)}
            </p>
          )}
          {asOfText && (
            <p className="sidebar-activity__asof">
              {copy.detail.asOf(asOfText)}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
